import { useState } from 'react'
import { gameStatus, TOOL_VERBS, toolDone } from '@aurora/core'
import { useAuroraStore } from '../../store/auroraStore'

/**
 * What the agent is doing, in plain words.
 *
 * A spinner says "wait". This says "I am reading six observations about
 * catching" — which is the difference between opaque and trustworthy.
 */
const VERBS = TOOL_VERBS

/** The tools whose step is a game being made: their line follows the game, not the moment the tool returned. */
const MAKES = new Set(['make_game', 'retry_game'])

/**
 * A step that made a game, said from where that game stands now ("Made
 * version 3"), so a reply read after the game is ready never sits under
 * "Making a game". A request kept for the next version, or a step that did
 * not start anything, says that instead.
 */
function gameStep(call: any, artifacts: Record<string, any>): string | undefined {
  if (!MAKES.has(call.name) || call.status === 'running') return undefined
  const out = call.output ?? {}
  if (out.error) return call.name === 'retry_game' ? 'Could not try it again' : 'Did not start a game'
  if (out.status === 'waiting') return undefined
  const a = artifacts[out.artifact_id]
  if (!a || a.deleted) return undefined
  const v = (a.version ?? 1) > 1 ? `version ${a.version}` : undefined
  const status = gameStatus(a)
  if (status === 'ready') return v ? `Made ${v}` : 'Made a game'
  if (status === 'failed') return v ? `${cap(v)} did not come back` : 'The game did not come back'
  return v ? `Making ${v}` : 'Making a game'
}
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1)

function describe(call: any, artifacts: Record<string, any> = {}): string {
  const game = gameStep(call, artifacts)
  if (game) return game
  // A step that made something reads as done once it is, never "Making a game" above a finished one.
  const done = toolDone(call)
  if (done) return done
  const verb = VERBS[call.name] ?? call.name
  const input = call.input ?? {}
  if (call.name === 'write_artifact') return `${verb} ${input.kind ?? 'artifact'}: ${input.title ?? ''}`
  if (call.name === 'read_artifacts') return `${verb} ${input.kind ?? 'artifacts'}`
  if (call.name === 'search') return `${verb} for "${input.query ?? ''}"`
  if (call.name === 'remember') return `${verb}: ${input.statement ?? ''}`
  if (call.name === 'analyze_image') return `${verb} the drawing`
  if (call.name === 'update_rubric') {
    const n = call.output?.states ?? call.input?.states?.length
    return n ? `${verb} · ${n} skill${n === 1 ? '' : 's'} moved` : verb
  }
  if (call.name === 'write_plan') {
    const n = call.output?.blocks ?? 0
    return n ? `${verb} · ${n} blocks` : verb
  }
  return verb
}

export function ToolActivity({ calls }: { calls: any[] }) {
  const [open, setOpen] = useState(false)
  const running = calls.some((c) => c.status === 'running')
  // Only the games these steps made, so the line follows them and nothing else.
  const artifacts = useAuroraStore((s: any) => {
    const out: Record<string, any> = {}
    for (const c of calls) {
      const id = MAKES.has(c.name) ? c.output?.artifact_id : undefined
      if (id && s.artifacts[id]) out[id] = s.artifacts[id]
    }
    return out
  }) as Record<string, any>
  const say = (c: any) => describe(c, artifacts)

  return (
    <div className={`tools${running ? ' is-running' : ''}`}>
      <button className="tools-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="tools-dot" aria-hidden="true" />
        <span className="tools-summary">
          {calls.map(say).slice(0, 2).join(' · ')}
          {calls.length > 2 ? ` +${calls.length - 2}` : ''}
        </span>
      </button>
      {open && (
        <ul className="tools-detail">
          {calls.map((c, i) => (
            <li key={c.id ?? i} className={`tools-item is-${c.status ?? 'complete'}`}>
              <span className="tools-item-name">{say(c)}</span>
              {c.error && <span className="tools-item-error">{c.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
